import { Fragment, useCallback, useEffect, useState } from "react";
import { useUrlState } from "@/lib/useUrlState";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Loader2, RefreshCw, Upload, ChevronRight, ChevronDown, Plus, Mail,
  Send, Share, FileDown, FileWarning, AlertTriangle, Pencil, Lock, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Segmented from "@/components/Segmented";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";
import PackageDetail from "./PackageDetail";
import DiscrepancyPanel from "./DiscrepancyPanel";
import LogReturnDialog from "./LogReturnDialog";
import SubmitAllDialog from "./SubmitAllDialog";
import RevisionPanel, { STATUS_TONE } from "./RevisionPanel";
import ReviewPanel from "./ReviewPanel";
import UnmatchedReturns from "./UnmatchedReturns";
import FrameworkEmailDialog from "../../components/FrameworkEmailDialog";
import { openInSubBuilder } from "./navigation";

const SECTIONS = [["log", "Submittal log"], ["checks", "Discrepancies"]];
const d10 = (s) => (s || "").slice(0, 10) || "—";

/** SUB-1 — the log is Scope → Submittal → Revision. Rows are submittals (their live revision);
 *  revisions expand under the row, newest first, each with its full record. The former
 *  Documents pill is retired: returned copies open from the Returned column. */
export default function SubmittalsTab({ project, reload }) {
  const [data, setData] = useState(null);
  const [checks, setChecks] = useState(null);
  const [sectionParam, setSectionParam] = useUrlState("section", "log");
  const section = sectionParam === "documents" ? "log" : sectionParam;   // retired route → log
  const setSection = setSectionParam;
  const [openSeq, setOpenSeq] = useUrlState("pkg", "");
  const [revId, setRevId] = useState("");
  const [busy, setBusy] = useState(false);
  const [logging, setLogging] = useState(null);
  const [chase, setChase] = useState(null);
  const [submitAll, setSubmitAll] = useState(null);
  const [title, setTitle] = useState("");

  const load = useCallback(async () => {
    try { setData(await opsApi.submittalLog(project.id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load the submittals"); }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (section === "checks" && !checks) {
      opsApi.submittalDiscrepancies(project.id).then(setChecks)
        .catch(() => toast.error("Could not run the discrepancy checks"));
    }
  }, [section, checks, project.id]);

  const refresh = async () => { await load(); reload?.(); };

  const chaseGc = async (pkg) => {
    setBusy(true);
    try { setChase(await opsApi.emailDraft(project.id, "submittal_followup", pkg.id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not prepare that chase email"); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const res = await opsApi.submittalGenerate(project.id);
      toast.success(res.created.length
        ? `${res.created.length} package draft(s) generated from the FRS: ${res.created.join(", ")}`
        : "Every FRS group already has a package");
      if (res.flagged?.length) toast.info(`FRS changed — review: ${res.flagged.join(", ")}`);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Nothing could be generated"); }
    finally { setBusy(false); }
  };

  const create = async () => {
    setBusy(true);
    try {
      const pkg = await opsApi.submittalCreate(project.id, { title: title.trim() });
      setTitle("");
      toast.success(`${pkg.number} created`);
      await load();
      setOpenSeq(String(pkg.seq));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create that package"); }
    finally { setBusy(false); }
  };

  const openSubmitAll = async () => {
    setBusy(true);
    try {
      const d = await opsApi.submittalSubmitAllDraft(project.id);
      opsApi.submittalZipDownload(project.id, "unsent")
        .catch(() => toast.error("The packages ZIP could not be downloaded"));
      const to = (d.to || []).join(",");
      const cc = (d.cc || []).length ? `&cc=${encodeURIComponent(d.cc.join(","))}` : "";
      window.location.href = `mailto:${to}?subject=${encodeURIComponent(d.subject || "")}${cc}&body=${encodeURIComponent(d.body || "")}`;
      toast.success("ZIP downloaded — attach it to the email that just opened");
      setSubmitAll(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Nothing is ready to submit"); }
    finally { setBusy(false); }
  };

  const edit = (pkg) => openInSubBuilder({
    projectId: pkg.subbuilder_project_id, submittalId: pkg.subbuilder_submittal_id,
    number: pkg.number, opsProjectId: project.id,
    opsProjectName: project.identity?.name || project.display || "",
    jobNumber: project.identity?.job_number || project.job_number,
  });

  const openDoc = (id) => opsApi.openDocument(id).catch(() => toast.error("The file could not be opened"));

  if (!data) {
    return <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading submittals…
    </div>;
  }

  const s = data.summary;
  const allRevisions = data.scopes.flatMap((sc) => sc.submittals.flatMap((sub) => sub.revisions));
  const total = data.scopes.reduce((n, sc) => n + sc.submittals.length, 0);

  const returnedCell = (r) => {
    const docId = r.returned?.document_id || r.returned_document_id;
    if (docId) {
      return (
        <span className="inline-flex items-center gap-1.5">
          <span className={`rounded-full px-2 py-0.5 text-[11px] ${STATUS_TONE[r.status]}`}>{r.status_label}</span>
          <button onClick={(e) => { e.stopPropagation(); openDoc(docId); }} title="Open the returned copy"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#007BFF] text-white hover:bg-[#0069d9]"
                  data-testid={`ops-submittal-returned-open-${r.number}${r.revision ? `-r${r.revision}` : ""}`}>
            <FileDown className="h-3.5 w-3.5" />
          </button>
        </span>
      );
    }
    if (r.pending_return?.intake_id) {
      return <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800" data-testid={`ops-submittal-pending-${r.number}`}>pending confirm</span>;
    }
    if (r.status === "submitted") {
      return (
        <span className={`text-xs ${r.ageing?.overdue ? "font-semibold text-rose-700" : "text-slate-600"}`}>
          {r.ageing?.overdue ? `${r.ageing.days_late}d late` : "with the GC"}
          {r.ageing?.overdue && data.can_edit && (
            <button onClick={(e) => { e.stopPropagation(); chaseGc(r); }} disabled={busy} data-testid={`ops-submittal-chase-${r.number}`}
                    className="ml-2 inline-flex items-center gap-1 rounded-full border border-rose-200 bg-white px-2 py-0.5 text-[11px] font-medium text-rose-700 hover:bg-rose-50">
              <Mail className="h-3 w-3" /> Chase
            </button>
          )}
        </span>
      );
    }
    return <span className={`rounded-full px-2 py-0.5 text-[11px] ${STATUS_TONE[r.status]}`}>{r.status_label}</span>;
  };

  const revRow = (r, sub, isLive, open) => (
    <tr key={r.id} onClick={() => { if (isLive) { setOpenSeq(open ? "" : String(sub.seq)); setRevId(""); } else setRevId(revId === r.id ? "" : r.id); }}
        className={`cursor-pointer ${isLive ? "hover:bg-slate-50" : "bg-slate-50/60 text-slate-600 hover:bg-slate-100/70"} ${!isLive && revId === r.id ? "bg-sky-50/70" : ""}`}
        data-testid={`ops-submittal-row-${r.number}${r.revision ? `-r${r.revision}` : ""}`}>
      <td className={`px-3 py-2 font-mono text-xs ${isLive ? "" : "pl-9"}`}>
        <span className="inline-flex items-center gap-1">
          {isLive && (open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />)}
          {isLive ? r.number : `Rev ${r.revision}`}
        </span>
      </td>
      <td className="px-3 py-2">
        {isLive ? (sub.title || r.title) : <span className="text-xs text-slate-500">{r.superseded ? "superseded" : ""}</span>}
        {isLive && r.source === "auto" && <span className="ml-1.5 rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-500">auto</span>}
        {r.locked && <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-700" data-testid={`ops-submittal-locked-${r.number}`}><Lock className="h-2.5 w-2.5" /> read-only</span>}
        {isLive && r.cut_sheets_missing > 0 && <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-800" data-testid={`ops-submittal-cutsheets-${r.number}`}><FileWarning className="h-2.5 w-2.5" /> {r.cut_sheets_missing} cut sheet(s)</span>}
        {isLive && r.frs_changed && <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] text-violet-700" title={r.frs_change_note}><AlertTriangle className="h-2.5 w-2.5" /> FRS changed</span>}
        {r.proposed_status && <span className="ml-1.5 rounded-full border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[10px] text-sky-700" data-testid={`ops-submittal-proposed-${r.number}`}>proposed {r.proposed_status_label}</span>}
      </td>
      <td className="px-3 py-2 text-center text-xs tabular-nums">{r.revision || 0}{isLive && sub.revision_count > 1 ? <span className="text-slate-400"> / {sub.revision_count}</span> : null}</td>
      <td className="px-3 py-2 text-xs">{d10(r.submitted_at)}</td>
      <td className="px-3 py-2">{returnedCell(r)}</td>
      <td className="px-3 py-2 text-xs tabular-nums">{r.days_out ?? "—"}</td>
      <td className="px-3 py-2 text-xs">{r.action_label || "—"}</td>
      <td className="px-3 py-2 text-xs">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${r.ball_in_court === "gc" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-700"}`}
              data-testid={`ops-submittal-ball-${r.number}${r.revision ? `-r${r.revision}` : ""}`}>{r.ball_in_court}</span>
      </td>
      <td className="px-3 py-2 text-right">
        {isLive && (
          <button onClick={(e) => { e.stopPropagation(); edit(r); }} data-testid={`ops-submittal-edit-${r.number}`}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700">
            <Pencil className="h-3 w-3" /> Edit
          </button>
        )}
      </td>
    </tr>
  );

  const detail = (r) => (
    <tr key={`detail-${r.id}`}>
      <td colSpan={9} className="bg-slate-50/70 px-4 py-4">
        <Breadcrumbs className="mb-3" trail={[{ label: "Submittals", dropParam: "pkg", testId: "ops-pkg-breadcrumb-list" }, { label: `${r.number}${r.revision ? ` Rev ${r.revision}` : ""}` }]} />
        <RevisionPanel project={project} rev={r} statuses={data.statuses} canEdit={data.can_edit}
                       onChanged={refresh} onLogReturn={(rev) => setLogging(rev)} />
        <div className="mt-3">
          <ReviewPanel project={project} rev={r} canEdit={data.can_edit} onChanged={refresh} />
        </div>
        <div className="mt-4">
          <PackageDetail project={project} pkgId={r.id} canEdit={data.can_edit} onChanged={refresh}
                         onUploadReturned={() => setLogging(r)} />
        </div>
      </td>
    </tr>
  );

  return (
    <div className="space-y-4" data-testid="ops-submittals-tab">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {[["Approved", s.total ? `${s.approved} of ${s.total}` : "no submittals", "ops-submittal-approved"],
          ["Ready for review", `${s.ready_for_review} in SubBuilder`, "ops-submittal-ready"],
          ["Outstanding", `${s.outstanding} with the GC`, "ops-submittal-outstanding"],
          ["Overdue", `${s.overdue} past due back`, "ops-submittal-overdue"],
          ["Ready to send", `${s.finalized_unsent} finalized`, "ops-submittal-sendable"]]
          .map(([label, value, tid]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={tid}>
            <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
            <p className="mt-1 text-base font-semibold text-slate-900 md:text-lg">{value}</p>
          </div>
        ))}
      </div>

      {total === 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center" data-testid="ops-submittals-empty">
          <p className="text-sm text-slate-600">
            No packages yet. They generate themselves in SubBuilder from the Current FRS the moment it is Reconciled —
            a rough draft on purpose, so the review starts early.
          </p>
          <p className="mt-1 text-xs text-slate-400">FRS status: {data.frs_status}</p>
          {data.can_edit && (
            <Button size="sm" className="mt-3" onClick={generate} disabled={busy} data-testid="ops-submittal-generate">
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />} Generate submittals
            </Button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {data.can_edit && (
          <>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New submittal title" className="h-8 w-64 text-sm"
                   onKeyDown={(e) => { if (e.key === "Enter" && title.trim()) create(); }} data-testid="ops-submittal-new-title" />
            <Button size="sm" className="h-8 px-2.5 text-xs" onClick={create} disabled={busy || !title.trim()} data-testid="ops-submittal-new" title="New submittal">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            </Button>
          </>
        )}
        <Button variant="data" size="sm" className="text-xs" data-testid="ops-submittal-export"
                onClick={() => opsApi.submittalLogDownload(project.id).catch(() => toast.error("The log could not be downloaded"))}>
          <Share className="mr-1.5 h-3.5 w-3.5" /> Excel
        </Button>
        <Button variant="data" size="sm" className="text-xs" data-testid="ops-submittal-zip"
                onClick={() => opsApi.submittalZipDownload(project.id, "all").catch((e) => toast.error(e?.response?.data?.detail || "Nothing finalized to download"))}>
          <Share className="mr-1.5 h-3.5 w-3.5" /> ZIP
        </Button>
        {data.can_edit && (
          <>
            <Button variant="data" size="sm" className="text-xs" onClick={generate} disabled={busy} data-testid="ops-submittal-generate" title="Build packages from the FRS products">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Generate
            </Button>
            <Button variant="data" size="sm" className="text-xs" disabled={!allRevisions.length} data-testid="ops-submittal-returned" title="Log a return on a revision"
                    onClick={() => setLogging({ pick: true })}>
              <Upload className="mr-1.5 h-3.5 w-3.5" /> Log return
            </Button>
            {s.finalized_unsent > 0 && (
              <Button variant="data" size="sm" className="text-xs" onClick={openSubmitAll} disabled={busy} data-testid="ops-submittal-submit-all" title="Opens your email with the packages ready to attach">
                <Send className="mr-1.5 h-3.5 w-3.5" /> Submit all
              </Button>
            )}
          </>
        )}
        <Segmented className="ml-2" value={section} onChange={setSection}
                   options={SECTIONS.map(([k, label]) => ({ key: k, label, testId: `ops-submittal-section-${k}` }))} />
      </div>

      {section === "log" && (
        <UnmatchedReturns project={project} items={data.unmatched_returns} revisions={allRevisions} onLinked={refresh} />
      )}

      {section === "log" && total > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[1080px] text-sm" data-testid="ops-submittal-log">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Number</th>
                <th className="px-3 py-2 text-left">Title</th>
                <th className="px-3 py-2 text-center">Rev</th>
                <th className="px-3 py-2 text-left">Sent</th>
                <th className="px-3 py-2 text-left">Returned</th>
                <th className="px-3 py-2 text-left">Days out</th>
                <th className="px-3 py-2 text-left">Action</th>
                <th className="px-3 py-2 text-left">Ball in court</th>
                <th className="px-3 py-2 text-right">Edit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.scopes.map((sc) => (
                <Fragment key={sc.code}>
                  <tr className="bg-slate-100/80" data-testid={`ops-submittal-scope-${sc.code}`}>
                    <td colSpan={9} className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                      <span className="inline-flex items-center gap-1.5"><Layers className="h-3 w-3" /> {sc.code}{sc.name && sc.name !== sc.code ? ` — ${sc.name}` : ""}
                        {sc.spec_section && <span className="font-normal normal-case text-slate-400">· spec {sc.spec_section}</span>}
                        <span className="font-normal normal-case text-slate-400">· {sc.submittals.length} submittal(s)</span></span>
                    </td>
                  </tr>
                  {sc.submittals.map((sub) => {
                    const open = openSeq === String(sub.seq);
                    const selected = open ? (sub.revisions.find((r) => r.id === revId) || sub.live) : null;
                    return (
                      <Fragment key={sub.seq}>
                        {revRow(sub.live, sub, true, open)}
                        {open && sub.revisions.slice(1).map((r) => revRow(r, sub, false, open))}
                        {open && selected && detail(selected)}
                      </Fragment>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {section === "checks" && (
        checks ? <DiscrepancyPanel data={checks} />
          : <div className="flex items-center gap-2 py-8 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Comparing the FRS with the cut-sheet library and the estimate…</div>
      )}

      <p className="text-xs text-slate-500">
        SubBuilder is the home of every submittal — this log is the same record seen from the job. A revision's status
        moves on its own only when the stamp matches a phrase exactly; everything else waits for a person to confirm.
      </p>

      {chase && (
        <FrameworkEmailDialog projectId={project.id} draft={chase} onClose={() => setChase(null)}
                              onSent={() => { setChase(null); refresh(); }} />
      )}
      {submitAll && (
        <SubmitAllDialog projectId={project.id} draft={submitAll} onClose={() => setSubmitAll(null)}
                         onSent={() => { setSubmitAll(null); refresh(); }} />
      )}
      {logging && (
        <LogReturnDialog project={project} revision={logging.pick ? null : logging} revisions={allRevisions}
                         onClose={() => setLogging(null)} onDone={() => { refresh(); }} />
      )}
    </div>
  );
}
